import React, { useEffect, useState } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { DeviceLinkModel, useActiveInstallationContext } from 'src/Libs/installations'
import { toggleSmartMeterGatewayActivation } from 'src/Libs/flow/advanced-settings/overview/api/smart-meter.api'
import { TabButtonGroup } from 'src/Libs/inputs/react-hook-form'
import { useSmartMeterGatewayData } from 'src/Libs/installation-settings/hooks'
import { useFetchSmartMeterGateway } from 'src/Libs/flow/advanced-settings/overview/hooks/use-fetch-smart-meter-getaway'
import SkiDetails from 'src/Libs/installations/components/new-installation/configuration/em360/SkiDetails'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { useUserRoles } from 'src/Hooks/user'
import { useResolveSmartMeterGatewayLogs } from 'src/Libs/system-status/hooks/use-resolve-smart-meter-gateway-logs'
import { TrustSmgwSkiForm } from 'src/Libs/installation-settings/model/smart-meter-gateway.model'

interface Props {
  installationId?: number
  emcDeviceLink?: DeviceLinkModel
  storageId?: string
  isSettingsPage?: boolean
  isFarm?: boolean
  isDisabled?: boolean
  refetchSmartMeterGatewayDataForFarms?: () => Promise<void>
  isReadOnlyAccess: boolean
  systemSkiLabel: string
}

const SmartMeterGatewayPanel: React.FC<Props> = ({
  installationId,
  emcDeviceLink,
  storageId,
  isSettingsPage,
  isFarm = false,
  isDisabled,
  refetchSmartMeterGatewayDataForFarms,
  isReadOnlyAccess,
  systemSkiLabel,
}) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const { triggerAppRating } = useAppRatingContext()
  const { isUser } = useUserRoles()
  const { deviceLinksLoading } = useActiveInstallationContext()
  const isMiddlewareRequestEnabled = !!installationId && !!emcDeviceLink
  const isReadOnly = isReadOnlyAccess || isUser

  const {
    smartMeterGatewayData,
    isLoading,
    isTrusting,
    error,
    trustSmartMeterGatewaySki,
    updateSmartMeterGatewayData,
    refetchSmartMeterGatewayData,
  } = useSmartMeterGatewayData(storageId, !isFarm)
  const {
    data: middlewareData,
    refetch: refetchMiddleware,
    isLoading: isMiddlewareLoading,
    isError: isMiddlewareError,
  } = useFetchSmartMeterGateway(installationId, emcDeviceLink?.id.toString() ?? null, isMiddlewareRequestEnabled)

  const { resolveSmartMeterGatewayLogs } = useResolveSmartMeterGatewayLogs(installationId)

  const trustSkiAndResolveLogs = async (form: TrustSmgwSkiForm): Promise<void> => {
    await trustSmartMeterGatewaySki(form)
    // the trust itself succeeded, a failed log resolution must not be reported as a failed trust
    await resolveSmartMeterGatewayLogs(emcDeviceLink?.id).catch(error => console.error('could not resolve smart meter gateway logs', error))
  }

  const hasError = isSettingsPage || !!storageId ? !!error : !!error || isMiddlewareError

  const [isToggleUpdating, setIsToggleUpdating] = useState(false)

  const isEebusEnabled = storageId ? !!smartMeterGatewayData?.isEebusDetectionEnabled : !!middlewareData?.smartMeterGateway?.active
  const em360EebusSki = smartMeterGatewayData?.ski
  const smgwSki = smartMeterGatewayData?.availableSmgwSkis?.[0]

  const isInitialLoading = storageId ? isLoading && !error : isMiddlewareLoading && !isMiddlewareError

  const methods = useForm<{ smgActive: boolean }>({ defaultValues: { smgActive: false } })

  useEffect(() => {
    methods.reset({ smgActive: isEebusEnabled })
  }, [methods, isEebusEnabled])

  const persistMiddlewareState = async (active: boolean): Promise<boolean> => {
    if (isSettingsPage) {
      return true
    }
    if (!installationId || !emcDeviceLink) {
      return false
    }

    const saveResult = await toggleSmartMeterGatewayActivation(installationId, emcDeviceLink.id.toString(), !middlewareData, active, authFetch)
    if (saveResult.hasErrors) {
      throw saveResult.error ?? new Error('failed-to-save-smart-meter-gateway-settings')
    }

    return true
  }

  const rollbackMiddlewareState = async (previousActive: boolean): Promise<void> => {
    if (isSettingsPage || !installationId || !emcDeviceLink) {
      return
    }

    const rollbackResult = await toggleSmartMeterGatewayActivation(installationId, emcDeviceLink.id.toString(), false, previousActive, authFetch)
    if (rollbackResult.hasErrors) {
      console.error('could not rollback smart meter gateway middleware settings', rollbackResult.error)
    }
  }

  const transmitGatewayState = async (storageId: string, active: boolean, previousActive: boolean): Promise<void> => {
    try {
      await updateSmartMeterGatewayData({ storageId, value: { isEebusDetectionEnabled: active } })
      methods.setValue('smgActive', active)
      enqueueSuccessStack(t('installation-configuration.em360.smg-controlbox.save.success-transmitted'))
      if (isSettingsPage) {
        triggerAppRating()
      }
    } catch (error) {
      await rollbackMiddlewareState(previousActive)
      methods.setValue('smgActive', previousActive)
      enqueueErrorStack(t('installation-configuration.em360.smg-controlbox.save.error-transmit'))
      console.error('could not transmit smart meter gateway settings', error)
    }
  }

  const handleToggle = async (active: boolean): Promise<void> => {
    setIsToggleUpdating(true)
    const previousActive = isEebusEnabled

    try {
      const hasPersistedState = await persistMiddlewareState(active)
      if (!hasPersistedState) {
        return
      }

      if (storageId) {
        await transmitGatewayState(storageId, active, previousActive)
        return
      }

      methods.setValue('smgActive', active)
      enqueueSuccessStack(t('installation-configuration.em360.smg-controlbox.save.success'))
    } catch (error) {
      methods.setValue('smgActive', previousActive)
      enqueueErrorStack(t('installation-configuration.em360.smg-controlbox.save.error'))
      console.error('could not save smart meter gateway settings', error)
    } finally {
      if (!isSettingsPage) {
        await refetchMiddleware()
      }
      if (isFarm) {
        await refetchSmartMeterGatewayDataForFarms?.()
      } else {
        await refetchSmartMeterGatewayData()
      }

      setIsToggleUpdating(false)
    }
  }

  return (
    <AccordionItem title={t('installation-configuration.em360.smg-controlbox.title')} expanded accordionDetailsSx={{ px: 4, pt: 2, pb: 4 }}>
      {isInitialLoading ? (
        <Stack alignItems={'center'} justifyContent={'center'} sx={{ py: 0 }}>
          <CircularProgress />
        </Stack>
      ) : hasError ? (
        <SystemSettingsBlankView variant={'error'} />
      ) : (
        <Stack gap={4}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent={'space-between'} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
            <Typography variant={'body1'}>{t('installation-configuration.em360.smg-controlbox.smg-connection')}</Typography>

            <FormProvider {...methods}>
              <Box
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  borderRadius: theme => theme.shape.customBorderRadius.tabButton,
                }}
              >
                <OptionalTooltipWrapper titleKey={'installation-configuration.em360.smg-controlbox.only-one-child-allowed'} hasTooltip={isDisabled}>
                  <TabButtonGroup
                    name={'smgActive'}
                    options={[
                      { value: true, label: t('installation-configuration.em360.modbus-tcp.enabled') },
                      { value: false, label: t('installation-configuration.em360.modbus-tcp.disabled') },
                    ]}
                    onChange={value => handleToggle(value as boolean)}
                    styledContainerSx={{ backgroundColor: 'shades.shade04' }}
                    disabled={isToggleUpdating || isDisabled || isReadOnly}
                  />
                </OptionalTooltipWrapper>
              </Box>
            </FormProvider>
          </Stack>

          <SkiDetails
            isEebusEnabled={isEebusEnabled}
            em360EebusSki={em360EebusSki}
            smgSki={smgwSki}
            emcDeviceId={storageId}
            trustedSkis={smartMeterGatewayData?.trustedSmgwSkis ?? []}
            trustSmartMeterGatewaySki={trustSkiAndResolveLogs}
            // the smart meter gateway logs of the device link are resolved after trusting, so wait for the device links
            isDisabled={isTrusting || isReadOnly || deviceLinksLoading}
            systemSkiLabel={systemSkiLabel}
          />
        </Stack>
      )}
    </AccordionItem>
  )
}

export default SmartMeterGatewayPanel
